import {
  normalizeMessengerV2SuiteId,
  applyMessengerV2Snapshot,
  ensureMessengerV2ThreadReadyState,
  fetchMessengerV2ConversationSnapshot,
  resolveMessengerV2Manager,
} from './messengerV2MlsGateway.js';
import { isMessengerV2ReadOnlyGroup, isMessengerV2SendCapableGroup } from './groupReadiness.js';
import {
  createMessengerV2LinkPreviewEnvelope,
  createMessengerV2DeleteEnvelope,
  createMessengerV2EditEnvelope,
  createMessengerV2TextEnvelope,
  resolveMessengerV2EnvelopeDisplayText,
  resolveMessengerV2NotificationPreviewText,
  serializeMessengerV2Envelope,
} from './messageEnvelope.js';
import { encryptHistory, persistHistoryKey } from './messengerV2Platform.js';
import { isMessengerV2AuthoritativeStateConflict } from './mlsRecoveryError.js';

const DEFAULT_MLS_CIPHERSUITE = 0x004d;
const DEFAULT_STAGE_TIMEOUT_MS = 15000;
const SEND_READY_TIMEOUT_MS = 12000;
const VOICE_CALL_SIGNAL_CONTENT_TYPE = 'application/vnd.nettgrav.messenger-v2.call-signal+mls';
const MLS_PENDING_RUNTIME_BODY = 'Encrypted MLS message pending runtime ready.';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeReadinessState = (value) => {
  const normalized = toStringOrNull(value);
  return normalized ? normalized.toLowerCase() : null;
};

const normalizeContentType = (value) => {
  const normalized = toStringOrNull(value);
  return normalized ? normalized.toLowerCase() : null;
};

const isReadinessBlocked = (groupInfo = null) =>
  normalizeReadinessState(groupInfo?.readinessState ?? groupInfo?.readiness_state ?? null) ===
    'blocked' ||
  groupInfo?.recoveryBlocked === true ||
  groupInfo?.recovery_blocked === true;

const toFiniteEpoch = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

const resolveCanonicalMessageId = ({
  id = null,
  serverMsgId = null,
  serverSeq = null,
  clientMsgId = null,
} = {}) => {
  const normalizedServerMsgId = toStringOrNull(serverMsgId);
  if (normalizedServerMsgId) {
    return normalizedServerMsgId;
  }

  const normalizedServerSeq = toFiniteEpoch(serverSeq);
  if (normalizedServerSeq != null) {
    return `server-seq-${normalizedServerSeq}`;
  }

  return toStringOrNull(id) || `ack-${toStringOrNull(clientMsgId) || Date.now()}`;
};

const withStageTimeout = async (stage, promise, timeoutMs = DEFAULT_STAGE_TIMEOUT_MS) => {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`V2 send timed out during ${stage}`);
      error.code = 'MLS_SEND_STAGE_TIMEOUT';
      error.stage = stage;
      reject(error);
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const sendMessengerV2Message = async ({
  conversationId,
  conversation = null,
  groupId,
  currentUserId,
  currentUserName = null,
  clientId = null,
  plaintext,
  clientMsgId,
  manager = null,
  preparedSend = null,
  envelope = null,
  persistHistory = true,
  contentType = 'application/octet-stream',
  stageTimeoutMs = DEFAULT_STAGE_TIMEOUT_MS,
  encryptTimeoutMs = null,
  sendTimeoutMs = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  const normalizedPlaintext = toStringOrNull(plaintext);
  const normalizedClientMsgId = toStringOrNull(clientMsgId);

  if (!normalizedConversationId) throw new Error('conversationId is required');
  if (!normalizedGroupId) throw new Error('groupId is required');
  if (!normalizedCurrentUserId) throw new Error('currentUserId is required');
  if (!normalizedClientMsgId) throw new Error('clientMsgId is required');

  const resolvedEnvelope =
    envelope && typeof envelope === 'object'
      ? envelope
      : createMessengerV2TextEnvelope(normalizedPlaintext);
  const transportPlaintext = serializeMessengerV2Envelope(resolvedEnvelope);
  const displayBody = resolveMessengerV2EnvelopeDisplayText(resolvedEnvelope);
  const normalizedContentType = toStringOrNull(contentType) || 'application/octet-stream';
  const normalizedContentTypeKey = normalizeContentType(normalizedContentType);
  const isHiddenCallSignal =
    normalizedContentTypeKey === VOICE_CALL_SIGNAL_CONTENT_TYPE ||
    resolvedEnvelope?.kind === 'call_signal' ||
    resolvedEnvelope?.hidden === true ||
    displayBody === MLS_PENDING_RUNTIME_BODY;

  if (!transportPlaintext) throw new Error('plaintext is required');

  const initialSendContext =
    preparedSend ||
    (await ensureMessengerV2SendReady({
      conversationId: normalizedConversationId,
      conversation,
      groupId: normalizedGroupId,
      currentUserId: normalizedCurrentUserId,
      currentUserName,
      clientId,
      manager,
    }));

  const performSendAttempt = async (sendContext) => {
    const {
      resolvedManager,
      preferredCiphersuite,
      senderClientId,
      identityId,
      groupInfo,
      groupId: authoritativeGroupId,
    } = sendContext;
    const sendGroupId = toStringOrNull(authoritativeGroupId) || normalizedGroupId;

    if (!isMessengerV2SendCapableGroup(groupInfo, { clientId: senderClientId })) {
      const error = new Error('V2 thread is not send-capable yet');
      error.code = 'E2EE_NOT_READY';
      throw error;
    }

    const sendEpoch =
      toFiniteEpoch(groupInfo?.runtimeEpoch ?? groupInfo?.runtime_epoch ?? null) ??
      toFiniteEpoch(groupInfo?.epoch ?? groupInfo?.current_epoch ?? null) ??
      0;

    const encrypted = await withStageTimeout(
      'encrypt',
      resolvedManager.encrypt({
        groupId: sendGroupId,
        identityId,
        plaintext: transportPlaintext,
      }),
      encryptTimeoutMs ?? stageTimeoutMs,
    );
    if (!encrypted?.envelope_b64 && !encrypted?.ciphertext) {
      throw new Error('MLS encrypt did not return an envelope');
    }

    let historyPayload = null;
    let notificationPayload = null;
    if (persistHistory !== false) {
      try {
        const historyKeyB64 =
          typeof resolvedManager.exportHistoryKey === 'function'
            ? await resolvedManager.exportHistoryKey({
                conversationId: normalizedConversationId,
                groupId: sendGroupId,
                epoch: encrypted?.epoch ?? null,
              })
            : null;
        if (historyKeyB64) {
          await Promise.resolve(
            persistHistoryKey({
              conversationId: normalizedConversationId,
              keyB64: historyKeyB64,
            }),
          ).catch(() => null);
          const notificationPreviewText = isHiddenCallSignal
            ? ''
            : resolveMessengerV2NotificationPreviewText(resolvedEnvelope);
          [historyPayload, notificationPayload] = await Promise.all([
            encryptHistory({
              conversationId: normalizedConversationId,
              plaintext: transportPlaintext,
              keyB64: historyKeyB64,
            }),
            notificationPreviewText
              ? encryptHistory({
                  conversationId: normalizedConversationId,
                  plaintext: notificationPreviewText,
                  keyB64: historyKeyB64,
                })
              : null,
          ]);
        }
      } catch {}
    }

    const response = await withStageTimeout(
      'sendApplicationMessage',
      resolvedManager.sendApplicationMessage({
        groupId: sendGroupId,
        senderClientId,
        envelopeB64: encrypted.envelope_b64 || encrypted.ciphertext,
        epoch: encrypted?.epoch ?? sendEpoch,
        ciphersuite: encrypted?.ciphersuite ?? groupInfo?.ciphersuite ?? preferredCiphersuite,
        contentType: normalizedContentType,
        clientMsgId: normalizedClientMsgId,
        historyCiphertextB64: historyPayload?.history_ciphertext_b64 || null,
        historyIvB64: historyPayload?.history_iv_b64 || null,
        historyAlg: historyPayload?.history_alg || null,
        notificationCiphertextB64: notificationPayload?.history_ciphertext_b64 || null,
        notificationIvB64: notificationPayload?.history_iv_b64 || null,
        notificationAlg: notificationPayload?.history_alg || null,
        internalReadyFlow: false,
      }),
      sendTimeoutMs ?? stageTimeoutMs,
    );

    return {
      encrypted,
      groupInfo,
      preferredCiphersuite,
      resolvedManager,
      response,
      sendEpoch,
      sendGroupId,
      senderClientId,
    };
  };

  let sendAttempt;
  try {
    sendAttempt = await performSendAttempt(initialSendContext);
  } catch (error) {
    if (!isMessengerV2AuthoritativeStateConflict(error)) {
      throw error;
    }

    const conflict = error?.payload?.error ?? error?.payload ?? {};
    await Promise.resolve(
      (initialSendContext.resolvedManager ?? manager)?.recoverAuthoritativeRuntime?.({
        conversationId: normalizedConversationId,
        groupId: initialSendContext.groupId ?? normalizedGroupId,
        authoritativeEpoch: conflict.authoritative_epoch ?? conflict.authoritativeEpoch ?? null,
        authoritativeCiphersuite:
          conflict.authoritative_ciphersuite ?? conflict.authoritativeCiphersuite ?? null,
        reason: conflict.code ?? error?.code ?? 'mls_authoritative_state_conflict',
      }),
    );

    const refreshedSendContext = await ensureMessengerV2SendReady({
      conversationId: normalizedConversationId,
      conversation,
      groupId: normalizedGroupId,
      currentUserId: normalizedCurrentUserId,
      currentUserName,
      clientId: initialSendContext.senderClientId ?? clientId,
      manager: initialSendContext.resolvedManager ?? manager,
    });
    sendAttempt = await performSendAttempt(refreshedSendContext);
  }

  const {
    encrypted,
    groupInfo,
    preferredCiphersuite,
    resolvedManager,
    response,
    sendEpoch,
    sendGroupId,
    senderClientId,
  } = sendAttempt;
  await Promise.resolve(
    resolvedManager.rememberSuccessfulSendRuntime?.({
      groupId: sendGroupId,
      groupHandle:
        groupInfo?.runtimeHandle ??
        groupInfo?.runtime_handle ??
        groupInfo?.groupHandle ??
        groupInfo?.group_handle ??
        null,
      epoch: encrypted?.epoch ?? sendEpoch,
      ciphersuite: encrypted?.ciphersuite ?? groupInfo?.ciphersuite ?? preferredCiphersuite,
    }),
  ).catch(() => null);
  const returnedBody = isHiddenCallSignal ? '' : displayBody;
  const returnedRawText = isHiddenCallSignal ? '' : normalizedPlaintext || displayBody;

  return {
    id: resolveCanonicalMessageId({
      id: response?.id ?? null,
      serverMsgId: response?.server_msg_id ?? response?.serverMsgId ?? null,
      serverSeq: response?.server_seq ?? response?.serverSeq ?? null,
      clientMsgId: normalizedClientMsgId,
    }),
    conversationId: normalizedConversationId,
    body: returnedBody,
    rawText: returnedRawText,
    messageKind: isHiddenCallSignal
      ? 'call_signal'
      : toStringOrNull(resolvedEnvelope?.kind ?? null) || 'text',
    hidden: isHiddenCallSignal,
    silent: isHiddenCallSignal,
    contentType: normalizedContentType,
    content_type: normalizedContentType,
    linkPreview: resolvedEnvelope?.preview || null,
    quote: resolvedEnvelope?.quote || null,
    callEvent: resolvedEnvelope?.callEvent || null,
    caption:
      resolvedEnvelope?.kind === 'attachment'
        ? toStringOrNull(resolvedEnvelope.caption ?? null)
        : null,
    attachments:
      resolvedEnvelope?.kind === 'attachment' && Array.isArray(resolvedEnvelope.attachments)
        ? resolvedEnvelope.attachments
        : [],
    createdAt: new Date().toISOString(),
    authorId: normalizedCurrentUserId,
    authorName: currentUserName || 'You',
    mine: true,
    optimistic: false,
    delivered: false,
    read: false,
    clientMsgId: response?.client_msg_id ?? normalizedClientMsgId,
    client_msg_id: response?.client_msg_id ?? normalizedClientMsgId,
    serverMsgId: toStringOrNull(response?.server_msg_id ?? response?.serverMsgId ?? null),
    server_msg_id: toStringOrNull(response?.server_msg_id ?? response?.serverMsgId ?? null),
    senderClientId,
    serverSeq: Number(response?.server_seq ?? response?.serverSeq ?? 0) || 0,
    state: 'sent',
  };
};

export const editMessengerV2Message = async ({
  conversationId,
  conversation = null,
  groupId,
  currentUserId,
  currentUserName = null,
  clientId = null,
  target,
  plaintext,
  clientMsgId,
  linkPreview = null,
  manager = null,
  preparedSend = null,
  envelope = null,
} = {}) =>
  sendMessengerV2Message({
    conversationId,
    conversation,
    groupId,
    currentUserId,
    currentUserName,
    clientId,
    plaintext,
    clientMsgId,
    manager,
    preparedSend,
    envelope:
      envelope && envelope.kind === 'edit'
        ? envelope
        : createMessengerV2EditEnvelope({
            target,
            text: plaintext,
            preview: linkPreview,
          }),
  });

export const deleteMessengerV2Message = async ({
  conversationId,
  conversation = null,
  groupId,
  currentUserId,
  currentUserName = null,
  clientId = null,
  target,
  clientMsgId,
  manager = null,
  preparedSend = null,
} = {}) =>
  sendMessengerV2Message({
    conversationId,
    conversation,
    groupId,
    currentUserId,
    currentUserName,
    clientId,
    plaintext: '',
    clientMsgId,
    manager,
    preparedSend,
    envelope: createMessengerV2DeleteEnvelope({
      target,
    }),
  });

export const sendMessengerV2LinkPreviewMessage = async ({
  conversationId,
  conversation = null,
  groupId,
  currentUserId,
  currentUserName = null,
  clientId = null,
  plaintext,
  clientMsgId,
  linkPreview = null,
  manager = null,
  preparedSend = null,
} = {}) =>
  sendMessengerV2Message({
    conversationId,
    conversation,
    groupId,
    currentUserId,
    currentUserName,
    clientId,
    plaintext,
    clientMsgId,
    manager,
    preparedSend,
    envelope: createMessengerV2LinkPreviewEnvelope({
      text: plaintext,
      preview: linkPreview,
    }),
  });

export async function ensureMessengerV2SendReady({
  conversationId,
  conversation = null,
  groupId,
  currentUserId,
  currentUserName = null,
  clientId = null,
  manager = null,
} = {}) {
  const normalizedConversationId = toStringOrNull(conversationId);
  const requestedGroupId = toStringOrNull(groupId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);

  if (!normalizedConversationId) throw new Error('conversationId is required');
  if (!requestedGroupId) throw new Error('groupId is required');
  if (!normalizedCurrentUserId) throw new Error('currentUserId is required');

  const preferredCiphersuite =
    normalizeMessengerV2SuiteId(
      conversation?.mls_ciphersuite ??
        conversation?.meta?.mls_ciphersuite ??
        conversation?.ciphersuite ??
        conversation?.meta?.ciphersuite ??
        null,
    ) ?? DEFAULT_MLS_CIPHERSUITE;
  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ??
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite,
      wantPQ: true,
      serverManaged: true,
    });
  if (!resolvedManager) {
    throw new Error('MLS manager unavailable for V2 send');
  }

  const ensuredIdentity =
    (await withStageTimeout(
      'ensureIdentity',
      resolvedManager.ensureIdentity({
        ciphersuite: preferredCiphersuite,
        name: identityName,
      }),
    ).catch(() => null)) || null;
  const ensuredClientId = toStringOrNull(
    ensuredIdentity?.clientId ?? ensuredIdentity?.client_id ?? null,
  );
  const requestedClientId = toStringOrNull(clientId);
  if (ensuredClientId && requestedClientId && ensuredClientId !== requestedClientId) {
    const error = new Error('The active MLS device does not match the requested sending device');
    error.code = 'E2EE_DEVICE_MISMATCH';
    throw error;
  }
  const authoritativeClientId =
    ensuredClientId ||
    requestedClientId ||
    toStringOrNull(resolvedManager.identityClientId ?? null);
  const authoritativeSnapshot = await withStageTimeout(
    'conversationSnapshot',
    fetchMessengerV2ConversationSnapshot({
      conversationId: normalizedConversationId,
      clientId: authoritativeClientId,
    }),
  );
  const authoritativeGroupId = toStringOrNull(
    authoritativeSnapshot?.group_id ?? authoritativeSnapshot?.groupId ?? null,
  );
  if (!authoritativeGroupId) {
    const error = new Error('Conversation MLS group is unavailable');
    error.code = 'E2EE_GROUP_UNAVAILABLE';
    throw error;
  }
  await applyMessengerV2Snapshot({
    manager: resolvedManager,
    snapshot: authoritativeSnapshot,
    userId: normalizedCurrentUserId,
    identityName,
    preferredCiphersuite,
    source: 'messages_v2_send_authoritative',
    conversationId: normalizedConversationId,
  });

  const threadState = await withStageTimeout(
    'ensureThreadReadyState',
    ensureMessengerV2ThreadReadyState({
      manager: resolvedManager,
      conversation,
      conversationId: normalizedConversationId,
      groupId: authoritativeGroupId,
      currentUserId: normalizedCurrentUserId,
      currentUserName: identityName,
      clientId: authoritativeClientId,
      preferredCiphersuite,
      reason: 'messages_v2_send',
      waitForReady: true,
      waitTimeoutMs: SEND_READY_TIMEOUT_MS,
      allowRecovery: true,
      allowPendingWelcomeJoin: true,
    }),
  );

  if (!threadState) {
    const error = new Error('V2 thread readiness could not be established');
    error.code = 'E2EE_NOT_READY';
    error.groupId = authoritativeGroupId;
    throw error;
  }

  const sendManager = threadState?.resolvedManager ?? resolvedManager;

  const senderClientId =
    toStringOrNull(threadState?.senderClientId ?? null) ||
    authoritativeClientId ||
    toStringOrNull(sendManager.identityClientId ?? null);
  const identityId = toStringOrNull(threadState?.identityId ?? null) || senderClientId;
  const groupInfo = threadState?.groupInfo ?? threadState?.readyInfo ?? null;

  if (isReadinessBlocked(groupInfo)) {
    const error = new Error(
      toStringOrNull(
        groupInfo?.readinessReason ??
          groupInfo?.readiness_reason ??
          groupInfo?.recoveryBlockedReason ??
          groupInfo?.recovery_blocked_reason ??
          'V2 thread recovery is blocked',
      ) || 'V2 thread recovery is blocked',
    );
    error.code = 'E2EE_RECOVERY_BLOCKED';
    error.groupId = authoritativeGroupId;
    error.readinessState = 'blocked';
    throw error;
  }

  if (!isMessengerV2SendCapableGroup(groupInfo, { clientId: senderClientId })) {
    const readOnly = isMessengerV2ReadOnlyGroup(groupInfo);
    const error = new Error(
      readOnly
        ? 'You can view previous messages, but you cannot send new messages in this group.'
        : 'V2 thread is not ready for send yet',
    );
    error.code = readOnly ? 'E2EE_READ_ONLY' : 'E2EE_NOT_READY';
    error.groupId = authoritativeGroupId;
    throw error;
  }

  return {
    resolvedManager: sendManager,
    preferredCiphersuite,
    senderClientId,
    identityId,
    groupInfo,
    groupId: authoritativeGroupId,
  };
}

const messengerV2MessageSendService = {
  ensureMessengerV2SendReady,
  sendMessengerV2Message,
};

export default messengerV2MessageSendService;
